import type { Map as MlMap } from "maplibre-gl";

/**
 * NASA GIBS MODIS/VIIRS NRT Global Flood raster overlay (optical, daily composites).
 *
 * A second, independent satellite source alongside GISTDA (Sentinel-1 SAR). Served
 * straight from NASA GIBS as public WMTS/XYZ tiles — no API key, no backend proxy.
 *   https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/{Layer}/default/{Time}/{TMS}/{z}/{y}/{x}.png
 *
 * Verified against the live service (12 ต.ค. 2569):
 *   · layer ids  MODIS_Combined_Flood_{1,2,3}-Day  → HTTP 200 PNG
 *   · TileMatrixSet GoogleMapsCompatible_Level9     → native max zoom 9 (Level12 ⇒ HTTP 400)
 *   · Time "default" = latest available composite    · format image/png · 256 px tiles
 *   · a z9 tile over mainland SE-Asia returns real (non-empty) flood pixels
 * (These identifiers must come from the live GIBS GetCapabilities — never guessed.)
 *
 * Limitation (shown to the owner in task #1493): this is an OPTICAL product — monsoon
 * cloud hides the surface and it cannot see under forest canopy, so "no colour ≠ safe".
 * 250 m is coarse vs GISTDA/Sentinel-1 (~10–30 m). It is a complementary daily view.
 */
export type NasaFloodWindow = "1day" | "2days" | "3days";
const WINDOWS: NasaFloodWindow[] = ["1day", "2days", "3days"];

/** GIBS WMTS layer identifier for each composite window (from live GetCapabilities). */
const GIBS_LAYER: Record<NasaFloodWindow, string> = {
  "1day": "MODIS_Combined_Flood_1-Day",
  "2days": "MODIS_Combined_Flood_2-Day",
  "3days": "MODIS_Combined_Flood_3-Day",
};

const GIBS_BASE = "https://gibs.earthdata.nasa.gov/wmts/epsg3857/best";
const TMS = "GoogleMapsCompatible_Level9"; // native max zoom = 9 for the 250 m flood product
const NATIVE_MAX_ZOOM = 9;                 // MapLibre over-zooms (scales) beyond this; never request z>9 (⇒ HTTP 400)
const ATTRIB =
  'น้ำท่วม (ดาวเทียม NASA): <a href="https://www.earthdata.nasa.gov/data/instruments/viirs/near-real-time-data/nrt-global-flood-products" target="_blank" rel="noopener">NASA GIBS · MODIS/VIIRS NRT Global Flood</a>';

const added = new Set<NasaFloodWindow>();

function layerId(w: NasaFloodWindow): string { return `nasa-flood-${w}`; }
function sourceId(w: NasaFloodWindow): string { return `nasa-flood-src-${w}`; }

/** REST/XYZ tile template. {z}/{y}/{x} = TileMatrix/TileRow/TileCol (GoogleMapsCompatible = XYZ, no scheme flip). */
function tileUrl(w: NasaFloodWindow): string {
  return `${GIBS_BASE}/${GIBS_LAYER[w]}/default/default/${TMS}/{z}/{y}/{x}.png`;
}

/** First label (symbol) layer, so the raster sits beneath road/place names (same as GISTDA). */
function firstSymbolLayerId(map: MlMap): string | undefined {
  for (const l of map.getStyle().layers ?? []) if (l.type === "symbol") return l.id;
  return undefined;
}

function ensure(map: MlMap, w: NasaFloodWindow): void {
  if (added.has(w)) return;
  const src = sourceId(w);
  if (!map.getSource(src)) {
    map.addSource(src, {
      type: "raster",
      tiles: [tileUrl(w)],
      tileSize: 256,
      // cap at the product's native zoom so MapLibre scales (over-zooms) instead of
      // requesting z>9 tiles, which GIBS rejects with HTTP 400.
      maxzoom: NATIVE_MAX_ZOOM,
      attribution: ATTRIB,
    });
  }
  const before = firstSymbolLayerId(map) ?? (map.getLayer("rivers-glow") ? "rivers-glow" : undefined);
  map.addLayer({
    id: layerId(w), type: "raster", source: src, layout: { visibility: "none" },
    paint: {
      // keep the native GIBS flood palette (don't recolour satellite-derived data);
      // 'nearest' shows the 250 m pixels as-is rather than interpolating new values.
      "raster-opacity": 0.8,
      "raster-resampling": "nearest",
    },
  }, before);
  added.add(w);
}

export function addNasaFloodLayers(map: MlMap): void {
  for (const w of WINDOWS) ensure(map, w);
}

/** Show one composite window (or hide all when null). */
export function setNasaFloodWindow(map: MlMap, window: NasaFloodWindow | null): void {
  for (const w of WINDOWS) {
    ensure(map, w);
    const id = layerId(w);
    if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", w === window ? "visible" : "none");
  }
}
